<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  procedureId: {
    type: String,
    default: null,
  },
  initialData: {
    type: Object,
    default: () => ({}),
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'save']);

const name = ref('');
const fields = ref([]);

watch(
  () => props.show,
  (nextValue) => {
    if (nextValue) {
      if (props.initialData && props.procedureId) {
        name.value = props.procedureId;
        const spec = props.initialData;
        const order = spec.order || [];
        const specFields = spec.fields || {};
        fields.value = order.map(k => ({
          key: k,
          label_en: specFields[k]?.label_en || '',
          label_th: specFields[k]?.label_th || '',
          detector: specFields[k]?.detector || 'username',
        }));
      } else {
        name.value = '';
        fields.value = [];
      }
    }
  },
  { immediate: true }
);

const addField = () => {
  fields.value.push({ key: '', label_en: '', label_th: '', detector: 'username' });
};

const removeField = (index) => {
  fields.value.splice(index, 1);
};

const canSubmit = computed(() => {
  return name.value.trim() && fields.value.length > 0 && fields.value.every(f => f.key.trim());
});

const buildPayload = () => {
  if (!canSubmit.value) return null;

  const spec = {
    max_questions_per_turn: 2,
    order: fields.value.map(f => f.key.trim()),
    fields: fields.value.reduce((acc, f) => {
      acc[f.key.trim()] = {
        label_en: f.label_en,
        label_th: f.label_th,
        detector: f.detector,
      };
      return acc;
    }, {}),
  };

  return {
    id: props.procedureId || name.value.trim(),
    newName: name.value.trim(),
    spec,
  };
};

const onSave = () => {
  const payload = buildPayload();
  if (!payload) return;
  emit('save', payload);
};

const closeSheet = () => {
  emit('close');
};

defineExpose({
  save: onSave,
  buildPayload,
  canSave: () => canSubmit.value,
});
</script>

<template>
  <teleport to="body">
    <transition name="article-sheet-fade">
      <div v-if="show" class="article-sheet-overlay" @click.self="closeSheet">
        <aside class="article-sheet fin-ai">
          <div class="article-sheet__surface">
            <div class="article-sheet__header">
              <div class="article-sheet__header-title-wrap">
                <h1 class="heading o__size-1 u__one-truncated-line">
                  {{ procedureId ? 'Edit procedure' : 'New procedure' }}
                </h1>
              </div>
              <div class="article-sheet__header-actions">
                <button class="btn o__secondary o__in-right-list" type="button" :disabled="isSaving" @click="closeSheet">Cancel</button>
                <button class="btn o__primary text-white article-sheet__cta" type="button" :disabled="!canSubmit || isSaving" :class="{ o__disabled: !canSubmit || isSaving }" @click="onSave">
                  {{ isSaving ? 'Saving...' : 'Save' }}
                </button>
              </div>
            </div>

            <div class="article-sheet__body">
              <div class="article-sheet__body-inner">
                <div class="mb-6">
                  <label class="block text-sm font-medium text-slate-700 mb-1">Procedure Name (Intent)</label>
                  <input v-model="name" type="text" class="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. bonus_claim" :disabled="!!procedureId" />
                </div>

                <div class="mb-6">
                  <div class="flex items-center justify-between mb-2">
                    <label class="block text-sm font-medium text-slate-700">Steps (Fields to collect)</label>
                    <button type="button" class="text-sm text-blue-600 font-medium hover:underline" @click="addField">+ Add step</button>
                  </div>
                  
                  <div v-if="!fields.length" class="text-sm text-slate-500 italic py-4 text-center border border-dashed border-slate-300 rounded-lg">
                    No steps added yet.
                  </div>
                  
                  <div v-for="(field, index) in fields" :key="index" class="border border-slate-200 rounded-lg p-4 mb-3 bg-slate-50 relative">
                    <button type="button" class="absolute top-2 right-2 text-slate-400 hover:text-red-500" @click="removeField(index)">
                      <span class="i-lucide-x size-4" />
                    </button>
                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-xs font-medium text-slate-600 mb-1">Field Key</label>
                        <input v-model="field.key" type="text" class="w-full border border-slate-300 rounded px-2 py-1 text-sm" placeholder="e.g. username" />
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-slate-600 mb-1">Detector</label>
                        <select v-model="field.detector" class="w-full border border-slate-300 rounded px-2 py-1 text-sm">
                          <option value="username">Username</option>
                          <option value="amount">Amount</option>
                          <option value="promo_ref">Promo Ref</option>
                          <option value="time_hint">Time Hint</option>
                          <option value="slip">Slip/Image</option>
                          <option value="any">Any Text</option>
                        </select>
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-slate-600 mb-1">Label (EN)</label>
                        <input v-model="field.label_en" type="text" class="w-full border border-slate-300 rounded px-2 py-1 text-sm" placeholder="e.g. Username" />
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-slate-600 mb-1">Label (TH)</label>
                        <input v-model="field.label_th" type="text" class="w-full border border-slate-300 rounded px-2 py-1 text-sm" placeholder="e.g. ยูสเซอร์" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Completion actions are temporarily hidden until the backend execution pipeline is implemented.
                </div>

              </div>
            </div>
          </div>
        </aside>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.article-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(15, 23, 42, 0.2);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: flex-end;
}

.article-sheet {
  width: min(800px, 88vw);
  height: 100vh;
  background: #ffffff;
  box-shadow: -28px 0 60px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(229, 231, 235, 0.9);
}

.article-sheet__surface {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--fin-base, #ffffff);
}

.article-sheet__header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 72px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--fin-neutral-border, #e9eae6);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
}

.article-sheet__header-title-wrap .heading {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.article-sheet__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.article-sheet__body {
  flex: 1;
  overflow: auto;
}

.article-sheet__body-inner {
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding: 28px 20px 52px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
}

.btn.o__secondary {
  border-color: #e5e7eb;
  background: #ffffff;
  color: #111827;
  padding: 6px 12px;
}

.btn.o__primary {
  background: #111827;
  color: #ffffff;
  padding: 6px 16px;
}

.btn.o__disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.article-sheet-fade-enter-active,
.article-sheet-fade-leave-active {
  transition: opacity 0.18s ease;
}

.article-sheet-fade-enter-from,
.article-sheet-fade-leave-to {
  opacity: 0;
}
</style>
